import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import {
    BonusStatusEnum,
    BonusTypeEnum,
    TRADE_TYPE,
    WALLET_ACCOUNT_TYPES,
} from 'src/common/constants';
import {
    changeScrollBehavior,
    currencyFormatter,
    digits,
    getDecimalPartCount,
} from 'src/common/helpers';
import { Button, Tooltip } from 'src/components/core';
import { Edit, EyeClose, EyeOpen, Info, Transfer } from 'src/components/core/icons';
import { FlexContainer, StyledFlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch, useAppSelector, useHiddenAssets } from 'src/hooks';
import { useKycAndFeaturesConfig } from 'src/hooks/useKycAndFeaturesConfig';
import { fetchAvailableAssets } from 'src/setup/redux/slices/derivativesSlice';
import { TokenBalance } from 'src/types';
import { RiskLimit } from 'src/types/configV2';

import { ErrorBoundary } from '../../common/error/ErrorBoundary';
import { TransferModal } from '../../common/modals/transferModal';
import { RiskLimitModal } from '../modals/riskLimitModal';

const StyledAsset = styled(StyledFlexContainer)`
    padding: 16px 11px;
    flex-direction: column;
    ${({ theme: { colors } }) => `
        border-bottom: 1px solid ${colors.border.light};
        border-top: 1px solid ${colors.border.light};
    `}
    & > div {
        margin-top: 8px;
        &:first-of-type {
            margin-bottom: 8px;
            margin-top: 0;
        }
    }
`;

const LabelText = styled(StyledFlexContainer)<{ color: string }>`
    ${({ theme: { typography }, color }) => `
        font-size: ${typography.size.desktop.paragraph.p3}; 
        line-height: calc(${typography.lineHeight[2]} - 1px)};
        &.heading {
            font-size: ${typography.size.desktop.paragraph.p2};
            line-height: calc(${typography.lineHeight[3]} - 0.5px);
            font-weight: ${typography.weight.medium}
        }
        color: ${color}
    `}
`;

const Assets: FC = () => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const dispatch = useAppDispatch();
    const { allowTrading } = useKycAndFeaturesConfig();

    const { currentSymbol, assets, tradeable, symbolSettings, error, loading } = useAppSelector(
        (state) => state.derivatives,
    );
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);
    const { assets: userAssets, userBonus } = useAppSelector((state) => state.user);

    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [isRiskLimitModalOpen, setIsRiskLimitModalOpen] = useState(false);
    const [currentRiskInfo, setCurrentRiskInfo] = useState({} as RiskLimit);
    const [buyRiskInfo, setBuyRiskInfo] = useState({} as RiskLimit);
    const [sellRiskInfo, setSellRiskInfo] = useState({} as RiskLimit);
    const [side, setSide] = useState('');
    const [bonusBalance, setBonusBalance] = useState({} as TokenBalance);

    const { isAssetsHidden, toggleIsAssetsHidden } = useHiddenAssets();

    const symbolTradeableInfo = tradeable.find((item) => currentSymbol.symbolId === item.tokenId);
    const fetchAvailableAssetsError = error?.fetchAvailableAssets !== undefined;
    const fetchAvailableAssetsLoading = loading?.fetchAvailableAssets;

    useEffect(() => {
        symbolSettings?.riskLimit.map((item) => {
            currentSymbol?.baseTokenFutures?.riskLimits.map((riskLimit) => {
                if (item.side == TRADE_TYPE.BUY_OPEN) {
                    if (item.riskLimitId == riskLimit.riskLimitId) {
                        setBuyRiskInfo(item);
                    }
                }
                if (item.side == TRADE_TYPE.SELL_OPEN) {
                    if (item.riskLimitId == riskLimit.riskLimitId) {
                        setSellRiskInfo(item);
                    }
                }
            });
        });
    }, [currentSymbol, symbolSettings]);

    useEffect(() => {
        if (userAssets?.subAccountBalances?.length > 0) {
            const subAccount = userAssets?.subAccountBalances?.find((bonus) => {
                return (
                    bonus.isBonusAccount &&
                    bonus.accountType === WALLET_ACCOUNT_TYPES.PERPETUALS_ACCOUNT
                );
            });
            const balance = subAccount?.balance.find(
                (bonusBalance) => bonusBalance.tokenId === 'USDT',
            );
            setBonusBalance(balance);
        }
    }, [userAssets]);

    const handleAssetsVisibility = () => {
        toggleIsAssetsHidden();
    };

    const handleTransferModal = () => {
        setIsTransferModalOpen(!isTransferModalOpen);
        changeScrollBehavior(!isTransferModalOpen);
    };

    const handleRiskLimitModal = (side?: string) => {
        if (side) {
            if (side == TRADE_TYPE.BUY_OPEN) {
                setCurrentRiskInfo(buyRiskInfo);
                setSide(TRADE_TYPE.BUY_OPEN);
            } else {
                setCurrentRiskInfo(sellRiskInfo);
                setSide(TRADE_TYPE.SELL_OPEN);
            }
        }

        setIsRiskLimitModalOpen(!isRiskLimitModalOpen);
        changeScrollBehavior(!isRiskLimitModalOpen);
    };

    const fetchDerivativesAvailableAssets = () => {
        dispatch(fetchAvailableAssets({ account_index: userCurrentFutureTradeAccountIndex }));
    };

    return (
        <>
            <StyledAsset>
                <FlexContainer styles={{ width: '100%', justifyContent: 'space-between' }}>
                    <FlexContainer styles={{ gap: 4 }}>
                        <LabelText className={'heading'} color={colors.text.primary}>
                            {t('assets')}
                        </LabelText>
                        {isAssetsHidden ? (
                            <EyeClose
                                fill={colors.text.tertiary}
                                className={'clickable'}
                                onClick={handleAssetsVisibility}
                            />
                        ) : (
                            <EyeOpen
                                fill={colors.text.tertiary}
                                className={'clickable'}
                                onClick={handleAssetsVisibility}
                            />
                        )}
                    </FlexContainer>
                    <FlexContainer>
                        <ErrorBoundary
                            loading={fetchAvailableAssetsLoading}
                            hasError={fetchAvailableAssetsError}
                            retryAction={fetchDerivativesAvailableAssets}
                        />
                        <FlexContainer
                            disabled={!allowTrading}
                            styles={{ gap: 8 }}
                            className={'clickable Transfer'}
                            onClick={!allowTrading ? undefined : handleTransferModal}
                        >
                            <Transfer stroke={colors.primary} />
                            <LabelText color={colors.primary}>{t('transfer')}</LabelText>
                        </FlexContainer>
                    </FlexContainer>
                </FlexContainer>

                <FlexContainer styles={{ width: '100%', justifyContent: 'space-between' }}>
                    <FlexContainer>
                        <LabelText color={colors.text.tertiary}>{t('total_equity')}</LabelText>
                    </FlexContainer>
                    <FlexContainer>
                        <LabelText color={colors.text.primary}>
                            {isAssetsHidden
                                ? `----`
                                : assets.total
                                ? currencyFormatter(
                                      digits(
                                          Number(assets.total),
                                          getDecimalPartCount(Number(assets.total)),
                                      ),
                                      getDecimalPartCount(Number(assets.total)),
                                      getDecimalPartCount(Number(assets.total)),
                                  )
                                : '--'}{' '}
                            {currentSymbol.quoteTokenId}
                        </LabelText>
                    </FlexContainer>
                </FlexContainer>
                {userBonus?.type === BonusTypeEnum.FUTURES_ACCOUNT &&
                    userBonus?.status === BonusStatusEnum.ACTIVE && (
                        <FlexContainer styles={{ width: '100%', justifyContent: 'space-between' }}>
                            <FlexContainer styles={{ gap: '8px' }}>
                                <LabelText color={colors.text.tertiary}>
                                    {t('futures_bonus')}
                                </LabelText>
                                <Tooltip overlay={t('futures_bonus_tooltip_text')} interactive>
                                    <Info fill={colors.text.tertiary} size={14} />
                                </Tooltip>
                            </FlexContainer>
                            <FlexContainer>
                                <LabelText color={colors.text.primary}>
                                    {isAssetsHidden
                                        ? `----`
                                        : bonusBalance.free
                                        ? currencyFormatter(
                                              digits(
                                                  Number(bonusBalance.free),
                                                  getDecimalPartCount(Number(bonusBalance.free)),
                                              ),
                                              getDecimalPartCount(Number(bonusBalance.free)),
                                              getDecimalPartCount(Number(bonusBalance.free)),
                                          )
                                        : '--'}{' '}
                                    {currentSymbol.quoteTokenId}
                                </LabelText>
                            </FlexContainer>
                        </FlexContainer>
                    )}
                <FlexContainer styles={{ width: '100%', justifyContent: 'space-between' }}>
                    <FlexContainer>
                        <LabelText color={colors.text.tertiary}>{t('available_balance')}</LabelText>
                    </FlexContainer>
                    <FlexContainer>
                        <LabelText color={colors.text.primary}>
                            {isAssetsHidden
                                ? `----`
                                : assets.availableMargin
                                ? currencyFormatter(
                                      digits(
                                          Number(assets.availableMargin),
                                          getDecimalPartCount(Number(assets.availableMargin)),
                                      ),
                                      getDecimalPartCount(Number(assets.availableMargin)),
                                      getDecimalPartCount(Number(assets.availableMargin)),
                                  )
                                : '--'}{' '}
                            {currentSymbol.quoteTokenId}
                        </LabelText>
                    </FlexContainer>
                </FlexContainer>
                <FlexContainer
                    styles={{
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <FlexContainer>
                        <LabelText color={colors.text.tertiary}>{t('risk_limit')}</LabelText>
                    </FlexContainer>
                    <FlexContainer styles={{ flexDirection: 'column', alignItems: 'end' }}>
                        <LabelText color={colors.text.primary}>
                            {`${
                                Number(symbolTradeableInfo?.longTotal)
                                    ? currencyFormatter(Number(symbolTradeableInfo?.longTotal), 1)
                                    : '--'
                            } / ${
                                Number(buyRiskInfo.riskLimitAmount)
                                    ? currencyFormatter(Number(buyRiskInfo.riskLimitAmount), 1)
                                    : '--'
                            } ${t('qty')} (${t('long')})`}
                            &nbsp;
                            <Button
                                type="button"
                                size="small"
                                variant="secondaryOutlined"
                                styles={{
                                    padding: '0px !important',
                                    borderRadius: '3px !important',
                                }}
                                onClick={() => handleRiskLimitModal(TRADE_TYPE.BUY_OPEN)}
                            >
                                <Edit fill={colors.text.tertiary} />
                            </Button>
                        </LabelText>
                        <LabelText color={colors.text.primary} style={{ marginTop: 8 }}>
                            {`${
                                Number(symbolTradeableInfo?.shortTotal)
                                    ? currencyFormatter(Number(symbolTradeableInfo?.shortTotal), 1)
                                    : '--'
                            } / ${
                                Number(sellRiskInfo.riskLimitAmount)
                                    ? currencyFormatter(Number(sellRiskInfo.riskLimitAmount), 1)
                                    : '--'
                            } ${t('qty')} (${t('short')})`}
                            &nbsp;
                            <Button
                                type="button"
                                size="small"
                                variant="secondaryOutlined"
                                styles={{
                                    padding: '0px !important',
                                    borderRadius: '3px !important',
                                }}
                                onClick={() => handleRiskLimitModal(TRADE_TYPE.SELL_OPEN)}
                            >
                                <Edit fill={colors.text.tertiary} />
                            </Button>
                        </LabelText>
                    </FlexContainer>
                </FlexContainer>
            </StyledAsset>
            {isTransferModalOpen && (
                <TransferModal open={isTransferModalOpen} handleClose={handleTransferModal} />
            )}
            {isRiskLimitModalOpen && (
                <RiskLimitModal
                    open={isRiskLimitModalOpen}
                    handleClose={handleRiskLimitModal}
                    currentRiskInfo={currentRiskInfo}
                    side={side}
                />
            )}
        </>
    );
};

export { Assets };
